import React, { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  LinearProgress,
  CircularProgress,
  Alert,
  Step,
  Stepper,
  StepLabel,
  StepContent,
  IconButton,
  Tooltip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Skeleton
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import InfoIcon from '@mui/icons-material/Info'
import ShareIcon from '@mui/icons-material/Share'
import FolderIcon from '@mui/icons-material/Folder'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { getCurrentUser } from '../../../auth/ducks/selectors'
import APIService from '../../../../services/APIService'
import SearchableSelect from 'components/SearchableSelect'
import ApproverPicker from 'components/ApproverPicker'
import organisationService from 'services/organisationService'
import DomainListField, { domainListError, serverDomainError } from './DomainListField'
import {
  getSharePointRedirectUri,
  getApiUrl
} from '../../../../utils/sharepoint'

const formatLibraryCount = count => {
  if (count === null || count === undefined) return null
  if (count === 0) return 'Empty'
  return count === 1 ? '1 item' : `${count} items`
}

export const withApprovers = (payload, approverIds) => (
  approverIds && approverIds.length > 0 ? { ...payload, approver_user_ids: approverIds } : payload
)

export const approverErrorMessage = error => {
  if (error?.response?.status !== 422) return null
  const errors = error.response.data?.errors || {}
  return errors.userIds?.[0] || errors.approverUserIds?.[0] || errors['approverUserIds.0']?.[0] || null
}

const SharePointIntegrationModal = ({
  open,
  onClose,
  buildingPassportId,
  onSuccess
}) => {
  const api = APIService()
  const [activeStep, setActiveStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [viewName, setViewName] = useState('')
  const [siteUrl, setSiteUrl] = useState('')
  const [selectedSite, setSelectedSite] = useState(null)
  const [selectedDrive, setSelectedDrive] = useState(null)
  const [authUrl, setAuthUrl] = useState('')
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [tenantId, setTenantId] = useState('')
  const [selectedFolderPath, setSelectedFolderPath] = useState('')
  const [selectedFolderWebUrl, setSelectedFolderWebUrl] = useState(null)
  const [browseFolders, setBrowseFolders] = useState([])
  const [currentPath, setCurrentPath] = useState('')
  const [folderLoading, setFolderLoading] = useState(false)
  const [libraries, setLibraries] = useState([])
  const [selectedDriveId, setSelectedDriveId] = useState('')
  const [librariesLoading, setLibrariesLoading] = useState(false)
  const [accessType, setAccessType] = useState('domain')
  const [domainRestriction, setDomainRestriction] = useState('')
  const [userDomain, setUserDomain] = useState('')
  const [syncMode, setSyncMode] = useState('read_write')
  const [approverIds, setApproverIds] = useState([])
  const [approverColleagues, setApproverColleagues] = useState([])
  const [approverAccess, setApproverAccess] = useState('loading')
  const [approverError, setApproverError] = useState('')

  const [domainError, setDomainError] = useState(null)

  const dispatch = useDispatch()
  const currentUser = useSelector(getCurrentUser)

  // Get user's domain from Redux state
  useEffect(() => {
    if (open && currentUser && currentUser.email) {
      const domain = currentUser.email.split('@')[1]
      setUserDomain(domain)
      // Only set domain restriction if it's empty (don't override user's choice)
      if (!domainRestriction) {
        setDomainRestriction(domain)
      }
    }
  }, [open, currentUser])

  useEffect(() => {
    if (!open || activeStep !== 3 || approverAccess !== 'loading') return
    organisationService.colleagues('')
      .then(res => {
        setApproverColleagues(res?.data?.data || [])
        setApproverAccess('ready')
      })
      .catch(() => setApproverAccess('denied'))
  }, [open, activeStep, approverAccess])

  const steps = [
    {
      label: 'Configure Integration',
      description: 'Enter SharePoint site details and view name'
    },
    {
      label: 'Authorise Access',
      description: 'Grant permission to access SharePoint files'
    },
    {
      label: 'Select Folder',
      description: 'Choose which folder to sync from SharePoint'
    },
    {
      label: 'Control Access',
      description: 'Choose who can view the synced files'
    },
    {
      label: 'Complete Setup',
      description: 'Finalise integration and start sync'
    }
  ]

  const handleNext = () => {
    setActiveStep(prevStep => prevStep + 1)
  }

  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1)
  }

  const handleClose = () => {
    setActiveStep(0)
    setViewName('')
    setSiteUrl('')
    setSelectedSite(null)
    setSelectedDrive(null)
    setError(null)
    setClientId('')
    setClientSecret('')
    setTenantId('')
    setSelectedFolderPath('')
    setSelectedFolderWebUrl(null)
    setBrowseFolders([])
    setCurrentPath('')
    setLibraries([])
    setSelectedDriveId('')
    setLibrariesLoading(false)
    setAccessType('domain')
    setDomainRestriction('')
    setUserDomain('')
    setSyncMode('read_write')
    setApproverIds([])
    setApproverColleagues([])
    setApproverAccess('loading')
    setApproverError('')
    onClose()
  }

  const validateStep0 = () => {
    return (
      viewName.trim() &&
      siteUrl.trim() &&
      clientId.trim() &&
      clientSecret.trim() &&
      tenantId.trim()
    )
  }

  const initiateSharePointAuth = async () => {
    console.log('initiateSharePointAuth called with:', {
      buildingPassportId,
      clientId,
      clientSecret,
      tenantId,
      viewName,
      siteUrl
    })

    if (!validateStep0()) {
      setError('Please fill in all required fields')
      return
    }

    if (!buildingPassportId) {
      setError(
        'Building Passport ID is missing. Please refresh the page and try again.'
      )
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await api.post(
        `/building-passport/${buildingPassportId}/sharepoint/initiate`,
        {
          building_passport_id: buildingPassportId,
          client_id: clientId,
          client_secret: clientSecret,
          tenant_id: tenantId,
          site_url: siteUrl,
          redirect_uri: getSharePointRedirectUri(),
          sync_mode: syncMode
        }
      )

      // axios-case-converter converts snake_case to camelCase
      const data = response.data?.data || response.data

      if (data && data.authorizationUrl) {
        setAuthUrl(data.authorizationUrl)

        // Store state for callback
        sessionStorage.setItem(
          'sharepoint_integration_state',
          JSON.stringify({
            building_passport_id: buildingPassportId,
            view_name: viewName,
            site_url: siteUrl,
            step: 1
          })
        )

        handleNext()
      } else {
        setError('Failed to initiate SharePoint integration')
      }
    } catch (error) {
      console.error('API Error:', error)
      setError(
        'Network error: ' +
          (error.response?.data?.message || error.message || 'Unknown error')
      )
    } finally {
      setLoading(false)
    }
  }

  // Use useRef to store the message listener so it persists across renders
  const messageListenerRef = useRef(null)
  const listenerTimeoutRef = useRef(null)

  // Clean up listeners on unmount
  useEffect(() => {
    return () => {
      if (messageListenerRef.current) {
        window.removeEventListener('message', messageListenerRef.current)
      }
      if (listenerTimeoutRef.current) {
        clearTimeout(listenerTimeoutRef.current)
      }
    }
  }, [])

  const openSharePointAuth = () => {
    if (authUrl) {
      // Remove any existing listener before adding a new one
      if (messageListenerRef.current) {
        window.removeEventListener('message', messageListenerRef.current)
      }
      if (listenerTimeoutRef.current) {
        clearTimeout(listenerTimeoutRef.current)
      }

      const popup = window.open(
        authUrl,
        'sharepoint-auth',
        'width=800,height=600,scrollbars=yes,resizable=yes'
      )

      // Listen for messages from the popup
      const messageListener = event => {
        console.log('Received message in parent window:', event)
        console.log('Message origin:', event.origin)
        console.log('Expected origin:', window.location.origin)
        console.log('Message data:', event.data)

        // Temporarily accept from any origin for debugging
        // if (event.origin !== window.location.origin) {
        //   console.log('Message origin mismatch:', event.origin, 'vs', window.location.origin)
        //   return
        // }

        console.log('Message data type:', event.data?.type)

        if (event.data.type === 'sharepoint_integration_success') {
          console.log('SharePoint integration success message received!')
          // Authorization completed successfully, now we need to browse folders
          // Don't try to close popup due to COOP policy - it will close itself

          // Store tokens temporarily for folder browsing
          sessionStorage.setItem(
            'sharepoint_temp_tokens',
            JSON.stringify({
              access_token: event.data.access_token,
              refresh_token: event.data.refresh_token,
              expires_in: event.data.expires_in || 3600,
              site_id: event.data.site_id,
              drive_id: event.data.drive_id
            })
          )

          setActiveStep(2) // Move to folder selection step
          loadSharePointLibraries()

          // Clear any error from previous attempts
          setError(null)

          // Clean up this specific listener
          window.removeEventListener('message', messageListener)
          messageListenerRef.current = null
          if (listenerTimeoutRef.current) {
            clearTimeout(listenerTimeoutRef.current)
            listenerTimeoutRef.current = null
          }
        } else if (event.data.type === 'sharepoint_auth_token_missing') {
          // Session expired during SharePoint auth
          // Don't try to close popup due to COOP policy - user needs to close it manually
          setError(
            event.data.error ||
              'Your session has expired. Please close the popup window, log in and try again.'
          )

          // DON'T remove the listener or go back - let user retry with same auth URL
          // The listener stays active so when they click "Open SharePoint Authorization" again,
          // the success message will be handled properly
        }
      }

      window.addEventListener('message', messageListener)
      messageListenerRef.current = messageListener

      // Clean up listener after 10 minutes
      listenerTimeoutRef.current = setTimeout(() => {
        if (messageListenerRef.current) {
          window.removeEventListener('message', messageListenerRef.current)
          messageListenerRef.current = null
        }
        listenerTimeoutRef.current = null
      }, 600000) // 10 minutes
    }
  }

  const setTempDriveId = driveId => {
    const tempTokens = JSON.parse(
      sessionStorage.getItem('sharepoint_temp_tokens') || '{}'
    )
    tempTokens.drive_id = driveId
    sessionStorage.setItem('sharepoint_temp_tokens', JSON.stringify(tempTokens))
  }

  const loadSharePointLibraries = async () => {
    if (!buildingPassportId) {
      setError('Building Passport ID is missing')
      return
    }

    setLibrariesLoading(true)
    setError(null)

    try {
      const tempTokens = JSON.parse(
        sessionStorage.getItem('sharepoint_temp_tokens') || '{}'
      )

      const response = await api.post(
        `/building-passport/${buildingPassportId}/sharepoint/list-drives`,
        {
          client_id: clientId,
          client_secret: clientSecret,
          tenant_id: tenantId,
          access_token: tempTokens.access_token,
          site_id: tempTokens.site_id
        }
      )

      const data = response.data?.data || response.data
      const found = (data && data.libraries) || []
      setLibraries(found)

      const ids = found.map(lib => lib.id)
      let chosen
      if (data && data.defaultDriveId && ids.includes(data.defaultDriveId)) {
        chosen = data.defaultDriveId
      } else if (tempTokens.drive_id && ids.includes(tempTokens.drive_id)) {
        chosen = tempTokens.drive_id
      } else {
        chosen = (found[0] && found[0].id) || tempTokens.drive_id
      }

      setSelectedDriveId(chosen || '')
      if (chosen) {
        setTempDriveId(chosen)
      }
      loadSharePointFolders('')
    } catch (error) {
      const tempTokens = JSON.parse(
        sessionStorage.getItem('sharepoint_temp_tokens') || '{}'
      )
      setSelectedDriveId(tempTokens.drive_id || '')
      loadSharePointFolders('')
    } finally {
      setLibrariesLoading(false)
    }
  }

  const handleLibraryChange = driveId => {
    if (!driveId || driveId === selectedDriveId) return
    setSelectedDriveId(driveId)
    setTempDriveId(driveId)
    setSelectedFolderPath('')
    setSelectedFolderWebUrl(null)
    setCurrentPath('')
    setBrowseFolders([])
    loadSharePointFolders('')
  }

  const loadSharePointFolders = async (folderPath) => {
    if (!buildingPassportId) {
      setError('Building Passport ID is missing')
      return
    }

    setFolderLoading(true)
    setError(null)

    try {
      const tempTokens = JSON.parse(
        sessionStorage.getItem('sharepoint_temp_tokens') || '{}'
      )

      const response = await api.post(
        `/building-passport/${buildingPassportId}/sharepoint/browse-folders`,
        {
          client_id: clientId,
          client_secret: clientSecret,
          tenant_id: tenantId,
          access_token: tempTokens.access_token,
          site_id: tempTokens.site_id,
          drive_id: tempTokens.drive_id,
          folder_path: folderPath
        }
      )

      const data = response.data?.data || response.data

      if (data) {
        setBrowseFolders(data.folders || [])
        setCurrentPath(data.currentPath || '')
      } else {
        setError('Failed to load SharePoint folders')
      }
    } catch (error) {
      setError(
        'Failed to load folders: ' +
          (error.response?.data?.message || error.message)
      )
    } finally {
      setFolderLoading(false)
    }
  }

  const handleFolderSelect = (folderPath, folderWebUrl) => {
    setSelectedFolderPath(folderPath)
    setSelectedFolderWebUrl(folderWebUrl || null)
  }

  const handleFolderDoubleClick = folderPath => {
    loadSharePointFolders(folderPath)
  }

  const handleGoUpFolder = () => {
    const parentPath = currentPath
      ? currentPath.split('/').slice(0, -1).join('/')
      : ''
    loadSharePointFolders(parentPath)
  }

  const completeIntegration = async () => {
    setLoading(true)
    setError(null)
    setApproverError('')

    try {
      const tempTokens = JSON.parse(
        sessionStorage.getItem('sharepoint_temp_tokens') || '{}'
      )

      const response = await api.post(
        `/building-passport/${buildingPassportId}/sharepoint/complete`,
        withApprovers({
          building_passport_id: buildingPassportId,
          view_name: viewName,
          site_id: tempTokens.site_id,
          drive_id: tempTokens.drive_id,
          folder_path: selectedFolderPath,
          folder_web_url: selectedFolderWebUrl,
          access_token: tempTokens.access_token,
          refresh_token: tempTokens.refresh_token,
          expires_in: tempTokens.expires_in || 3600,
          client_id: clientId,
          client_secret: clientSecret,
          tenant_id: tenantId,
          access_type: accessType,
          domain_restriction: accessType === 'domain' ? domainRestriction : null,
          sync_mode: syncMode
        }, approverIds)
      )

      const data = response.data?.data || response.data

      if (data) {
        sessionStorage.removeItem('sharepoint_temp_tokens')
        sessionStorage.removeItem('sharepoint_integration_state')

        setActiveStep(4) // Move to completion step

        if (onSuccess) {
          onSuccess(data.customView)
        }
      } else {
        setError('Failed to complete integration')
      }
    } catch (error) {
      const approverMessage = approverErrorMessage(error)
      if (approverMessage) {
        setApproverError(approverMessage)
        return
      }
      const fieldError = serverDomainError(error)
      if (fieldError) {
        setDomainError(fieldError)
      } else {
        setError(
          'Integration failed: ' +
            (error.response?.data?.message || error.message)
        )
      }
    } finally {
      setLoading(false)
    }
  }

  const renderStepContent = step => {
    switch (step) {
      case 0:
        return (
          <StepContentWrapper data-tour-id='sharepoint.details'>
            <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
              Configure your SharePoint integration by providing the site URL
              and a name for this view.
            </Typography>

            <Alert severity='warning' sx={{ mb: 2 }}>
              <Typography
                variant='body2'
                sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}
              >
                ⚠ IMPORTANT - Please Read Carefully:
              </Typography>
              <Typography variant='body2' sx={{ mt: 1 }}>
                <strong>
                  1. This setup is ONLY for THIS Building Passport
                </strong>
                <br />
                • Each Building Passport needs its own SharePoint connection
                <br />
                • You will need to repeat this process for each Building
                Passport
                <br />• The folder you choose to sync from Sharepoint will
                appear only in this Building Passport
              </Typography>
              <Typography variant='body2' sx={{ mt: 1 }}>
                <strong>2. You will need the following information:</strong>
                <br />
                • Your SharePoint site URL (example:
                https://yourcompany.sharepoint.com/sites/your-site)
                <br />
                • Azure AD Application credentials (provided by your IT
                administrator)
                <br />• Permission to access the SharePoint documents
              </Typography>
            </Alert>

            <TextField
              fullWidth
              label='View Name'
              value={viewName}
              onChange={e => setViewName(e.target.value)}
              placeholder='e.g., Building Documents from SharePoint'
              required
              sx={{ mb: 2 }}
              helperText='This will be the name of the custom view containing your SharePoint files'
            />

            <TextField
              fullWidth
              label='SharePoint Site URL'
              value={siteUrl}
              onChange={e => setSiteUrl(e.target.value)}
              placeholder='https://yourcompany.sharepoint.com/sites/your-site'
              required
              sx={{ mb: 2 }}
              helperText='The URL of the SharePoint site containing your documents'
            />

            <Typography variant='subtitle2' sx={{ mt: 3, mb: 1 }}>
              Azure AD Application Credentials
            </Typography>

            <TextField
              fullWidth
              label='Application (Client) ID'
              value={clientId}
              onChange={e => setClientId(e.target.value)}
              placeholder='e.g., 12345678-1234-1234-1234-123456789012'
              required
              sx={{ mb: 2 }}
              helperText='Your Azure AD Application (client) ID'
            />

            <TextField
              fullWidth
              label='Client Secret'
              value={clientSecret}
              onChange={e => setClientSecret(e.target.value)}
              placeholder='Your client secret value'
              type='password'
              required
              sx={{ mb: 2 }}
              helperText='Your Azure AD Application client secret value'
            />

            <TextField
              fullWidth
              label='Tenant ID'
              value={tenantId}
              onChange={e => setTenantId(e.target.value)}
              placeholder='e.g., 12345678-1234-1234-1234-123456789012 or common'
              required
              sx={{ mb: 2 }}
              helperText="Your Azure AD Tenant ID (or 'common' for multi-tenant)"
            />

            <Typography variant='subtitle2' sx={{ mt: 3, mb: 1 }}>
              Sync Mode
            </Typography>

            <FormControl component='fieldset' sx={{ mb: 2 }}>
              <RadioGroup
                value={syncMode}
                onChange={e => setSyncMode(e.target.value)}
              >
                <FormControlLabel
                  value='read_write'
                  control={<Radio size='small' />}
                  label={
                    <Typography variant='body2'>
                      <strong>Read & Write (Recommended)</strong> — Allows future two-way sync between SharePoint and Building Passport
                    </Typography>
                  }
                />
                <FormControlLabel
                  value='read_only'
                  control={<Radio size='small' />}
                  label={
                    <Typography variant='body2'>
                      <strong>Read Only</strong> — One-way sync: files are copied from SharePoint but never written back
                    </Typography>
                  }
                />
                <FormControlLabel
                  value='one_time'
                  control={<Radio size='small' />}
                  label={
                    <Typography variant='body2'>
                      <strong>One-Time Import</strong> — Import files once from SharePoint. No ongoing sync — use this if you won't have continued access to the folder.
                    </Typography>
                  }
                />
              </RadioGroup>
            </FormControl>

            <Alert severity='info' sx={{ mb: 2 }}>
              <Box>
                <Typography variant='body2' sx={{ mb: 1 }}>
                  <strong>Azure AD App Setup Requirements:</strong>
                </Typography>
                <Typography variant='body2' component='div'>
                  • Register an app in Azure AD with Sites.Selected and
                  {syncMode === 'read_write' ? ' Files.ReadWrite.All' : ' Files.Read.All'} permissions
                  <br />• Add {window.location.origin}/sharepoint/callback as a
                  redirect URI
                  <br />
                  • Generate a client secret and note it (you won't be able to
                  see it again)
                  <br />
                  • Grant admin consent for the required permissions
                  <br />• Files will be synced while maintaining
                  SharePoint folder structure
                </Typography>
              </Box>
            </Alert>

            <Box display='flex' justifyContent='flex-end' gap={2}>
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                variant='contained'
                onClick={initiateSharePointAuth}
                disabled={!validateStep0() || loading}
              >
                {loading ? 'Setting up...' : 'Continue'}
              </Button>
            </Box>
          </StepContentWrapper>
        )

      case 1:
        return (
          <StepContentWrapper>
            <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
              Click the button below to authorise access to your SharePoint
              site. You'll be redirected to Microsoft's secure login page.
            </Typography>

            <Alert severity='warning' sx={{ mb: 2 }}>
              <Typography variant='body2'>
                <strong>Important:</strong> Make sure you have the necessary
                permissions to access the SharePoint site and grant application
                access.
              </Typography>
            </Alert>

            <AuthButton
              variant='contained'
              size='large'
              startIcon={<ShareIcon />}
              onClick={openSharePointAuth}
              disabled={!authUrl}
              data-tour-id='sharepoint.authorise'
            >
              Authorise SharePoint Access
            </AuthButton>

            <Typography variant='body2' color='textSecondary' sx={{ mt: 2 }}>
              After authorisation, this dialog will automatically proceed to the
              next step.
            </Typography>

            <Box display='flex' justifyContent='flex-start' gap={2} mt={3}>
              <Button onClick={handleBack}>Back</Button>
            </Box>
          </StepContentWrapper>
        )

      case 2:
        return (
          <StepContentWrapper>
            <Box sx={{ mb: 2 }}>
              <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
                Document library
              </Typography>
              <Typography
                variant='body2'
                color='textSecondary'
                sx={{ mb: 1 }}
              >
                A SharePoint site can contain several document libraries. Choose
                the one you want to sync, then pick a folder inside it below.
              </Typography>

              {librariesLoading ? (
                <Skeleton variant='rounded' height={44} />
              ) : libraries.length > 1 ? (
                <SearchableSelect
                  value={selectedDriveId}
                  onChange={handleLibraryChange}
                  options={libraries.map(lib => ({
                    value: lib.id,
                    label: lib.isDefault
                      ? `${lib.name} (default)`
                      : lib.name,
                    sublabel: [
                      lib.isDefault ? 'Default library' : null,
                      formatLibraryCount(lib.itemCount)
                    ]
                      .filter(Boolean)
                      .join(' · ') || null
                  }))}
                  placeholder='Select a document library'
                  searchPlaceholder='Search libraries…'
                  ariaLabel='SharePoint document library'
                />
              ) : libraries.length === 1 ? (
                <Alert severity='info' sx={{ py: 0.5 }}>
                  <Typography variant='body2'>
                    This site has one document library:{' '}
                    <strong>{libraries[0].name}</strong>
                  </Typography>
                </Alert>
              ) : null}
            </Box>

            <Alert severity='info' sx={{ mb: 2 }}>
              <Typography
                variant='body2'
                sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}
              >
                IMPORTANT - Understanding Folder Selection:
              </Typography>
              <Typography variant='body2' sx={{ mt: 1 }}>
                <strong>What happens when you select a folder:</strong>
              </Typography>
              <Typography variant='body2' sx={{ mt: 0.5, pl: 2 }}>
                • ALL files inside the selected folder will be synced
                <br />
                • ALL subfolders and their files will be included
                <br />
                • Files will update automatically when changed in SharePoint
                <br />• This becomes your "root" folder in Building Passport
              </Typography>
              <Typography variant='body2' sx={{ mt: 1 }}>
                <strong>Tips for choosing the right folder:</strong>
              </Typography>
              <Typography variant='body2' sx={{ mt: 0.5, pl: 2 }}>
                • Select "Root folder" to sync EVERYTHING from SharePoint
                <br />
                • Select a specific folder to sync only that section
                <br />
                • You CANNOT change this later without re-doing the setup
                <br />• Empty folders will NOT appear (SharePoint limitation)
              </Typography>
            </Alert>

            <Alert severity='warning' sx={{ mb: 2 }}>
              <Typography variant='body2'>
                <strong>How to navigate:</strong>
                <br />• <strong>Double-click</strong> a folder to open it and
                see what's inside
                <br />• <strong>Single-click</strong> a folder to select it for
                syncing
                <br />• Use the <strong>"Go Up"</strong> button to go back to
                the parent folder
              </Typography>
            </Alert>

            <Box sx={{ mb: 2 }}>
              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                Current Location: /{currentPath || 'Root'}
              </Typography>

              {currentPath && (
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={handleGoUpFolder}
                  size='small'
                  sx={{ mb: 1 }}
                >
                  Go Up
                </Button>
              )}
            </Box>

            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                maxHeight: 300,
                overflow: 'auto',
                mb: 2
              }}
            >
              {folderLoading ? (
                <Box display='flex' justifyContent='center' p={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : browseFolders.length === 0 ? (
                <Box p={2} textAlign='center'>
                  <Typography variant='body2' color='textSecondary'>
                    No folders found in this location
                  </Typography>
                </Box>
              ) : (
                browseFolders.map(folder => (
                    <Box
                      key={folder.id}
                      sx={{
                        p: 1,
                        cursor: 'pointer',
                        borderBottom: 1,
                        borderColor: 'divider',
                        backgroundColor:
                          selectedFolderPath === folder.path
                            ? 'action.selected'
                            : 'transparent',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                      onClick={() => handleFolderSelect(folder.path, folder.webUrl)}
                      onDoubleClick={() => handleFolderDoubleClick(folder.path)}
                    >
                      <Box display='flex' alignItems='center' gap={1}>
                        <FolderIcon color='primary' />
                        <Typography variant='body2'>{folder.name}</Typography>
                        {folder.childCount > 0 && (
                          <Typography variant='caption' color='textSecondary'>
                            ({folder.childCount} items)
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))

              )}
            </Box>

            <Alert severity='success' sx={{ mb: 2 }}>
              <Typography
                variant='body2'
                sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}
              >
                You have selected:{' '}
                {selectedFolderPath || 'Root folder (ENTIRE SharePoint library)'}
              </Typography>
              <Typography variant='body2' sx={{ mt: 1 }}>
                <strong>This means:</strong>
              </Typography>
              {!selectedFolderPath ? (
                <Typography variant='body2' sx={{ mt: 0.5, pl: 2 }}>
                  • ALL documents in your SharePoint library will be synced
                  <br />
                  • ALL folders and subfolders will be included
                  <br />
                  • This is the most comprehensive option
                  <br />• New files added to SharePoint will automatically
                  appear here
                </Typography>
              ) : (
                <Typography variant='body2' sx={{ mt: 0.5, pl: 2 }}>
                  • Only files in "{selectedFolderPath}" and its subfolders will
                  be synced
                  <br />
                  • Files outside this folder will NOT appear
                  <br />
                  • New files added to this folder in SharePoint will
                  automatically appear
                  <br />• You are limiting the sync to this specific section
                </Typography>
              )}
              <Typography variant='body2' sx={{ mt: 1, color: 'warning.dark' }}>
                <strong>⚠ Remember:</strong> You cannot change this selection
                later. To sync a different folder, you would need to delete this
                integration and start over.
              </Typography>
            </Alert>

            <Box display='flex' justifyContent='space-between' gap={2}>
              <Button onClick={handleBack}>Back</Button>
              <Button
                variant='contained'
                onClick={handleNext}
                disabled={!selectedFolderPath && currentPath !== ''}
              >
                Continue
              </Button>
            </Box>
          </StepContentWrapper>
        )

      case 3:
        return (
          <StepContentWrapper>
            <Typography variant='h6' sx={{ mb: 2 }}>
              4. Control Access
            </Typography>

            <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
              By default, all files synced from Sharepoint will be visible and
              viewable by users from the same domain as you. To change this,
              select from the following options below. Permissions to view files
              can also be individually controlled after upload.
            </Typography>

            <FormControl component='fieldset' sx={{ mb: 3 }}>
              <RadioGroup
                value={accessType}
                onChange={e => setAccessType(e.target.value)}
              >
                <FormControlLabel
                  value='domain'
                  control={<Radio sx={{ alignSelf: 'flex-start', mt: -0.5 }} />}
                  label={
                    <Box sx={{ flex: 1 }}>
                      <Typography variant='body2'>
                        <strong>(Default)</strong> The selected
                        Sharepoint/OneDrive folder/files viewable to all
                        existing and future users of this Building Passport
                        {' '}whose email address is at one of the domains below.
                      </Typography>
                      {accessType === 'domain' && (
                        <Box sx={{ mt: 1.5 }}>
                          <DomainListField
                            value={domainRestriction}
                            onChange={value => { setDomainRestriction(value); setDomainError(null) }}
                            userDomain={userDomain}
                            enabled={open}
                            serverError={domainError}
                          />
                        </Box>
                      )}
                    </Box>
                  }
                  sx={{ mb: 2, alignItems: 'flex-start', display: 'flex' }}
                />
                <FormControlLabel
                  value='private'
                  control={
                    <Radio sx={{ alignSelf: 'flex-start', mt: -1.25 }} />
                  }
                  label={
                    <Typography variant='body2'>
                      The selected Sharepoint/OneDrive folder/files viewable
                      only to you.
                    </Typography>
                  }
                  sx={{ mb: 2, alignItems: 'flex-start', display: 'flex' }}
                />
                <FormControlLabel
                  value='public'
                  control={<Radio sx={{ alignSelf: 'flex-start', mt: -1.5 }} />}
                  label={
                    <Typography variant='body2'>
                      The selected Sharepoint/OneDrive folder/files viewable to
                      all existing and future users of this Building Passport.
                    </Typography>
                  }
                  sx={{ alignItems: 'flex-start', display: 'flex' }}
                />
              </RadioGroup>
            </FormControl>

            <Box sx={{ mb: 3 }}>
              <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 0.5 }}>
                Who approves access requests for these documents?
              </Typography>
              <Typography variant='body2' color='textSecondary' sx={{ mb: 1.5, fontSize: 15 }}>
                An approver is a person who decides whether someone can open a locked document. Requests for SharePoint
                documents go to these people, not to the person who connected SharePoint. Leave this empty and your
                organisation&apos;s Super Admins decide, shown as &quot;Super Admins (default)&quot;.
              </Typography>
              {approverAccess === 'loading' && <Skeleton variant='rounded' height={44} />}
              {approverAccess === 'ready' && (
                <ApproverPicker value={approverIds} onChange={ids => { setApproverIds(ids); setApproverError('') }} colleagues={approverColleagues} />
              )}
              {approverAccess === 'denied' && (
                <Typography variant='body2' sx={{ fontSize: 15 }}>
                  Your organisation&apos;s Super Admins will approve requests.
                </Typography>
              )}
              {approverError && (
                <Alert severity='error' role='alert' sx={{ mt: 1.5 }}>
                  {approverError}
                </Alert>
              )}
            </Box>

            <Box display='flex' justifyContent='space-between' gap={2}>
              <Button onClick={handleBack}>Back</Button>
              <Button
                variant='contained'
                onClick={completeIntegration}
                disabled={loading || (accessType === 'domain' && !!domainListError(domainRestriction))}
              >
                {loading ? 'Completing...' : 'Complete Integration'}
              </Button>
            </Box>
          </StepContentWrapper>
        )

      case 4:
        return (
          <StepContentWrapper>
            <Box display='flex' alignItems='center' gap={2} mb={2}>
              <FolderIcon color='primary' />
              <Typography variant='h6'>Integration Complete!</Typography>
            </Box>

            <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
              Your SharePoint integration has been set up successfully. The
              initial sync is in progress.
            </Typography>

            <Alert severity='success' sx={{ mb: 2 }}>
              <Typography variant='body2'>
                • Files will be synced in the background
                <br />
                • Folder structure will be preserved
                <br />
                • Files are read-only and cannot be moved between folders
                <br />• Sync happens automatically to keep files up to date
              </Typography>
            </Alert>

            <LinearProgress sx={{ mb: 2 }} />
            <Typography variant='body2' color='textSecondary' align='center'>
              Initial sync in progress...
            </Typography>

            <Box display='flex' justifyContent='flex-end' mt={3}>
              <Button variant='contained' onClick={handleClose}>
                Done
              </Button>
            </Box>
          </StepContentWrapper>
        )

      default:
        return null
    }
  }

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
      <DialogHeader>
        <DialogTitle>
          <Box display='flex' alignItems='center' gap={2}>
            <ShareIcon color='primary' />
            SharePoint Integration
          </Box>
        </DialogTitle>
        <Tooltip title='Close'>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </DialogHeader>

      <DialogContent>
        {error && (
          <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Stepper activeStep={activeStep} orientation='vertical'>
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>
                <Typography variant='subtitle1' fontWeight='medium'>
                  {step.label}
                </Typography>
                <Typography variant='body2' color='textSecondary'>
                  {step.description}
                </Typography>
              </StepLabel>
              <StepContent>{renderStepContent(index)}</StepContent>
            </Step>
          ))}
        </Stepper>
      </DialogContent>
    </StyledDialog>
  )
}

export default SharePointIntegrationModal

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    max-height: 80vh;
  }
`

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px 0;
`

const StepContentWrapper = styled.div`
  padding: 16px 0;
`

const AuthButton = styled(Button)`
  && {
    background: linear-gradient(45deg, #0078d4 30%, #106ebe 90%);
    padding: 12px 24px;
    margin: 16px 0;

    &:hover {
      background: linear-gradient(45deg, #106ebe 30%, #005a9e 90%);
    }
  }
`
